import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useBrandKit } from '../contexts/BrandKitContext';
import clsx from 'clsx';

const { FiPlus, FiEdit3, FiTrash2, FiPalette, FiCheck } = FiIcons;

const BrandKitManager = ({ onCreateNew, onEdit }) => {
  const { brandKits, loading, deleteBrandKit, selectBrandKit, selectedKit } = useBrandKit();

  const handleDelete = async (kitId) => {
    if (window.confirm('Are you sure you want to delete this brand kit?')) {
      try {
        await deleteBrandKit(kitId);
      } catch (error) {
        console.error('Failed to delete brand kit:', error);
        alert('Failed to delete brand kit. Please try again.');
      }
    }
  };

  const handleSelect = (kit) => {
    selectBrandKit(kit);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading brand kits...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Action Bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Your Brand Kits</h2>
          <p className="text-gray-600">Manage your brand identity collections</p>
        </div>
        <button
          onClick={onCreateNew}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Create New Kit</span>
        </button>
      </div>

      {/* Brand Kits Grid */}
      {brandKits.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {brandKits.map((kit, index) => (
            <motion.div
              key={kit.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={clsx(
                "bg-white rounded-xl p-6 shadow-sm border-2 transition-all duration-200 hover:shadow-md",
                selectedKit?.id === kit.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              )}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiPalette} className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{kit.name || 'Untitled Kit'}</h3>
                    <p className="text-sm text-gray-600">{kit.description || 'No description'}</p>
                  </div>
                </div>
                {selectedKit?.id === kit.id && (
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              {/* Colors Preview */}
              {kit.colors && Object.keys(kit.colors).length > 0 && (
                <div className="flex space-x-2 mb-4">
                  {Object.entries(kit.colors).slice(0, 5).map(([key, color]) => (
                    <div
                      key={key}
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: color }}
                      title={key}
                    />
                  ))}
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <span>Updated {new Date(kit.updated_at).toLocaleDateString()}</span>
                <div className="flex items-center space-x-3">
                  {kit.logos && <span>{kit.logos.length} logo{kit.logos.length !== 1 ? 's' : ''}</span>}
                  {kit.watermarks && <span>{kit.watermarks.length} watermark{kit.watermarks.length !== 1 ? 's' : ''}</span>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleSelect(kit)}
                  className={clsx(
                    "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors",
                    selectedKit?.id === kit.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  {selectedKit?.id === kit.id ? 'Selected' : 'Select'}
                </button>
                <button
                  onClick={() => onEdit(kit)}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(kit.id)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
          <SafeIcon icon={FiPalette} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Brand Kits Yet</h3>
          <p className="text-gray-600 mb-4">Create your first brand kit to get started</p>
          <button
            onClick={onCreateNew}
            className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Create Brand Kit</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default BrandKitManager;